import type { ReactNode } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon, FilterIcon, InformationCircleIcon } from '@hugeicons/core-free-icons'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { OverviewTabs } from '@/components/shared/overview/OverviewBreakdownCard'
import {
  AnswerChip,
  KpiStrip,
  KpiTile,
  ReportSearch,
  ReportVerdict,
  reportTable,
} from '@/components/shared/analytics/AnalyticsReport'
import { AnalyticsEmpty } from '@/components/shared/analytics/AnalyticsEmpty'
import { CTR_TOOLTIP } from '@/lib/constants/metricsCopy'
import type { LandingSegment, NearestPage } from '@/lib/calculations/pagesReport'
import { describeEditDistance } from '@/lib/calculations/pagesReport'
import { formatDuration, formatNumeric } from '@/lib/utils/stringUtils'
import { cn } from '@/lib/utils'

/**
 * Presentational pieces of the Analytics → Pages tab (Paper "03 - App ·
 * Analytics": "Analytics · Pages · 1440", "· filtered to /pricing", "· Loading",
 * "· Filter with no results"). `Pages.tsx` owns the data and passes props; the
 * DEV states gallery renders the same pieces with Paper's mock numbers.
 */

export type PageListView = 'pages' | 'entry' | 'exit'

export const PAGE_LIST_OPTIONS: { value: PageListView; label: string }[] = [
  { value: 'pages', label: 'All pages' },
  { value: 'entry', label: 'Landing pages' },
  { value: 'exit', label: 'Exit pages' },
]

const pct = (share: number) => (share > 0 && share < 1 ? '<1' : String(Math.round(share)))

/* ─── Verdict ─────────────────────────────────────────────────────────── */

export type PagesVerdictProps =
  | { kind: 'all'; totalViews: number; totalPages: number; topPage: string | null; topShare: number; meta: string }
  | { kind: 'filtered'; page: string; views: number; share: number; meta: string }
  | { kind: 'noResults'; filterLabel: string; meta: string }

export function PagesVerdict(props: PagesVerdictProps) {
  if (props.kind === 'noResults') {
    return <ReportVerdict meta={props.meta}>No views match {props.filterLabel}.</ReportVerdict>
  }
  if (props.kind === 'filtered') {
    return (
      <ReportVerdict meta={props.meta}>
        <span className="min-w-0 break-all">
          {formatNumeric(props.views)} views on {props.page},
        </span>
        <AnswerChip>{pct(props.share)}%</AnswerChip>
        <span>of all views on the site</span>
      </ReportVerdict>
    )
  }
  const pagesNoun = props.totalPages === 1 ? 'page' : 'pages'
  return (
    <ReportVerdict meta={props.meta}>
      {props.topPage && props.totalViews > 0 ? (
        <>
          <span>
            {formatNumeric(props.totalViews)} total views across {formatNumeric(props.totalPages)} {pagesNoun}.
          </span>
          <AnswerChip>{pct(props.topShare)}%</AnswerChip>
          <span className="min-w-0 break-all">of them land on {props.topPage}</span>
        </>
      ) : (
        <span>No page views in this range yet.</span>
      )}
    </ReportVerdict>
  )
}

/* ─── Summary card: KPI tiles + "Where views land" ────────────────────── */

export type PagesKpi = {
  label: string
  value: ReactNode
  title?: string
  delta?: number | null
  deltaTitle?: string
}

export function WhereViewsLand({
  segments,
  totalViews,
  filteredPage,
}: {
  segments: LandingSegment[]
  totalViews: number
  /** Set while a page filter is active: the bar keeps the site's segments */
  filteredPage?: string | null
}) {
  const filtered = Boolean(filteredPage)
  const highlighted = segments.find((s) => s.highlighted)
  return (
    <div className="flex flex-col gap-3.5 px-5 pt-4 pb-5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[14.5px] font-semibold leading-[18px] text-foreground">Where views land</span>
        <span className="text-xs leading-4 text-muted-foreground">
          {filtered
            ? `Filtered to ${filteredPage}, shown against all ${formatNumeric(totalViews)} views on the site`
            : `Share of ${formatNumeric(totalViews)} total views by page`}
        </span>
      </div>
      {segments.length > 0 ? (
        <>
          <div
            className="flex h-3 shrink-0 gap-[3px]"
            role="img"
            aria-label={filtered ? `${filteredPage}: share of all views on the site` : 'Share of total views by page'}
          >
            {segments.map((seg) => (
              <span
                key={seg.key}
                className={cn('h-3 min-w-[3px] rounded-[4px]', seg.fill)}
                style={{ width: `${seg.share}%` }}
                title={`${seg.label}: ${formatNumeric(seg.views)} views`}
              />
            ))}
          </div>
          {filtered ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <LegendItem fill={highlighted?.fill} label={filteredPage!}>
                {pct(highlighted?.share ?? 0)}% · {formatNumeric(highlighted?.views ?? 0)} views
              </LegendItem>
              <LegendItem fill="bg-neutral-200 dark:bg-neutral-700" label="Rest of the site">
                {pct(100 - (highlighted?.share ?? 0))}%
              </LegendItem>
              <span className="hidden flex-1 sm:block" />
              <span className="text-xs leading-4 text-ink-3">Other pages stay grey so the filtered page keeps its place</span>
            </div>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {segments.map((seg) => (
                <LegendItem key={seg.key} fill={seg.fill} label={seg.label}>
                  {pct(seg.share)}%
                </LegendItem>
              ))}
            </div>
          )}
        </>
      ) : (
        <span className="text-[13px] text-muted-foreground">No page views in this range yet.</span>
      )}
    </div>
  )
}

function LegendItem({ fill, label, children }: { fill?: string; label: string; children: ReactNode }) {
  return (
    <span className="flex min-w-0 items-center gap-[7px]">
      <span className={cn('size-2 shrink-0 rounded-[2px]', fill)} />
      <span className="max-w-[220px] truncate text-xs leading-4 text-muted-foreground">{label}</span>
      <span className="text-xs font-medium leading-4 text-foreground tabular-nums">{children}</span>
    </span>
  )
}

export function PagesSummaryCard({ kpis, children }: { kpis: PagesKpi[]; children: ReactNode }) {
  return (
    <Card className="gap-0 py-0">
      <KpiStrip>
        {kpis.map((k, i) => (
          <KpiTile key={k.label} first={i === 0} label={k.label} value={k.value} title={k.title} delta={k.delta} deltaTitle={k.deltaTitle} />
        ))}
      </KpiStrip>
      {children}
    </Card>
  )
}

/* ─── List card: view toggle, explainer, search, section chips ─────────── */

export function PagesListToolbar({
  view,
  onViewChange,
  search,
  onSearchChange,
}: {
  view: PageListView
  onViewChange: (view: PageListView) => void
  search: string
  onSearchChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-3 px-[18px] py-3.5 lg:flex-row lg:items-center lg:gap-0">
      <OverviewTabs ariaLabel="Page list" options={PAGE_LIST_OPTIONS} value={view} onChange={onViewChange} className="self-start lg:self-auto" />
      <p className="min-w-0 flex-1 text-xs leading-4 text-ink-3 lg:pl-3">
        Landing: the first page of a visit. Exit: the last. Both follow your filters.
      </p>
      <ReportSearch value={search} onChange={onSearchChange} placeholder="Search pages" />
    </div>
  )
}

/** "root" → "Root", "direct" → "Direct": data labels read in sentence case (Paper). Paths and hosts stay as they are. */
export const sentenceLabel = (v: string | null | undefined) =>
  v && /^[a-z]/.test(v) && !/[./]/.test(v) ? v.charAt(0).toUpperCase() + v.slice(1) : (v ?? '')

/** Section filter chip: ink when active, white with a hairline ring otherwise. Never orange. */
export function SectionChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex h-[26px] shrink-0 items-center whitespace-nowrap rounded-md px-[9px] text-xs font-medium leading-4 tabular-nums transition-colors',
        active ? 'bg-foreground text-background' : 'bg-card text-foreground ring-1 ring-foreground/[0.08] hover:bg-muted/60',
      )}
    >
      {children}
    </button>
  )
}

export function SectionChipRow({ children }: { children: ReactNode }) {
  return (
    <div className="-mt-1 flex items-center gap-1.5 overflow-x-auto scrollbar-hide px-[18px] pb-3.5">
      <span className="shrink-0 pr-1 text-xs leading-4 text-muted-foreground">Section</span>
      {children}
    </div>
  )
}

/* ─── Top pages table ─────────────────────────────────────────────────── */

export type PagesTableRow = {
  page: string
  group?: string | null
  views: number
  visitors: number
  ctr: number | null
  referrer: string
  scrollPercent: number
  timeOnPageSeconds: number
}

export function PagesTableHead() {
  return (
    <TableHeader className="[&_tr]:border-b-0">
      <TableRow className={reportTable.headRow}>
        <TableHead className={cn(reportTable.th, 'min-w-[200px]')}>Page</TableHead>
        <TableHead className={cn(reportTable.th, 'w-[100px] text-right')}>Total views</TableHead>
        <TableHead className={cn(reportTable.th, 'hidden w-[110px] text-right sm:table-cell')}>Unique visitors</TableHead>
        <TableHead className={cn(reportTable.th, 'hidden w-[110px] text-right sm:table-cell')}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex items-center gap-1" aria-label="How clicks per visitor is calculated">
                  Clicks / visitor
                  <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/60" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                {CTR_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableHead>
        <TableHead className={cn(reportTable.th, 'hidden w-[140px] text-right sm:table-cell')}>Top referrer</TableHead>
        <TableHead className={cn(reportTable.th, 'hidden w-[100px] text-right md:table-cell')}>Scroll depth</TableHead>
        <TableHead className={cn(reportTable.th, 'hidden w-[100px] text-right lg:table-cell')}>Time on page</TableHead>
      </TableRow>
    </TableHeader>
  )
}

export const PAGES_TABLE_COLUMNS = 7

export function PagesTopTable({
  rows,
  expandedPage,
  onToggle,
  renderDetails,
  emptyText,
}: {
  rows: PagesTableRow[]
  expandedPage?: string | null
  onToggle?: (page: string) => void
  renderDetails?: (page: string) => ReactNode
  emptyText: string
}) {
  return (
    <Table>
      <PagesTableHead />
      <TableBody>
        {rows.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={PAGES_TABLE_COLUMNS} className={reportTable.empty}>
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.flatMap((row, index) => {
            const isExpanded = expandedPage === row.page
            return [
              <TableRow
                key={`row-${index}`}
                className={cn(reportTable.row, 'group cursor-pointer', isExpanded && 'bg-muted/40')}
                onClick={() => onToggle?.(row.page)}
                aria-expanded={isExpanded}
              >
                <TableCell className={cn(reportTable.td, 'max-w-0')} title={row.page}>
                  <div className="flex min-w-0 items-center gap-2">
                    <HugeiconsIcon
                      icon={ArrowDown01Icon}
                      size={14}
                      className={cn(
                        'shrink-0 text-muted-foreground/60 transition-transform duration-150 group-hover:text-foreground',
                        isExpanded && 'rotate-180',
                      )}
                    />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium leading-[18px] text-foreground">{row.page || '/'}</span>
                      {row.group && <span className="truncate text-xs leading-4 text-muted-foreground">{sentenceLabel(row.group)}</span>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className={cn(reportTable.td, reportTable.num)}>{formatNumeric(row.views)}</TableCell>
                <TableCell className={cn(reportTable.td, reportTable.num, 'hidden sm:table-cell')}>{formatNumeric(row.visitors)}</TableCell>
                <TableCell className={cn(reportTable.td, reportTable.num, 'hidden sm:table-cell')}>
                  {/* Clicks ÷ visitors is a count, not a rate: 8.48, never "848%". */}
                  {row.ctr == null ? '—' : (row.ctr / 100).toFixed(2)}
                </TableCell>
                <TableCell className={cn(reportTable.td, 'hidden text-right sm:table-cell')}>
                  <span
                    className="inline-flex h-[22px] max-w-[130px] items-center truncate rounded-md px-2 text-xs leading-[14px] text-muted-foreground ring-1 ring-foreground/[0.08]"
                    title={row.referrer}
                  >
                    <span className="truncate">{sentenceLabel(row.referrer)}</span>
                  </span>
                </TableCell>
                <TableCell className={cn(reportTable.td, reportTable.numMuted, 'hidden md:table-cell')}>
                  {Math.round(row.scrollPercent)}%
                </TableCell>
                <TableCell className={cn(reportTable.td, reportTable.numMuted, 'hidden lg:table-cell')}>
                  {formatDuration(row.timeOnPageSeconds)}
                </TableCell>
              </TableRow>,
              isExpanded && renderDetails ? (
                <TableRow key={`details-${index}`} className="border-b border-border/60 hover:bg-transparent">
                  <TableCell colSpan={PAGES_TABLE_COLUMNS} className="bg-muted/30 px-[18px] py-4">
                    <div onClick={(e) => e.stopPropagation()}>{renderDetails(row.page)}</div>
                  </TableCell>
                </TableRow>
              ) : null,
            ].filter(Boolean)
          })
        )}
      </TableBody>
    </Table>
  )
}

/* ─── Page filter with no results ─────────────────────────────────────── */

export function PagesFilterEmpty({
  filterLabel,
  periodNoun,
  nearest,
  onApplyNearest,
  onClear,
}: {
  /** "Page is /pricng" */
  filterLabel: string
  /** "this week" */
  periodNoun: string
  nearest: NearestPage | null
  onApplyNearest: (page: string) => void
  onClear: () => void
}) {
  return (
    <AnalyticsEmpty
      icon={FilterIcon}
      title={`No pages match ${filterLabel}`}
      description={`The filter hides every view ${periodNoun}, so every number on this tab reads zero.`}
      className="pb-10"
      descriptionClassName="max-w-[520px]"
    >
      {nearest && (
        <div className="mt-5 flex w-full max-w-[460px] flex-col overflow-clip rounded-xl bg-card text-left ring-1 ring-border">
          <div className="border-b border-border/60 px-3.5 py-2.5 text-xs font-medium leading-4 text-ui-meta">Closest page with views</div>
          <div className="flex items-center gap-3 px-3.5 py-2.5">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
              <span className="truncate text-sm font-medium leading-[18px] text-foreground">{nearest.page}</span>
              <span className="text-xs leading-4 text-muted-foreground tabular-nums">
                {formatNumeric(nearest.views)} views · {describeEditDistance(nearest.distance)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onApplyNearest(nearest.page)}
              className="flex h-8 max-w-[220px] shrink-0 items-center rounded-lg bg-primary px-3 text-[13px] font-medium leading-4 text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <span className="truncate">Filter to {nearest.page}</span>
            </button>
          </div>
        </div>
      )}
      <div className={cn('flex items-center gap-2', nearest ? 'mt-4' : 'mt-5')}>
        <span className="text-[13px] leading-4 text-muted-foreground">Or start over:</span>
        <button
          type="button"
          onClick={onClear}
          className="flex h-8 items-center rounded-lg bg-card px-3 text-[13px] font-medium leading-4 text-ui-ink ring-1 ring-input transition-colors hover:bg-muted/60"
        >
          Clear filter
        </button>
      </div>
    </AnalyticsEmpty>
  )
}

/* ─── Loading ─────────────────────────────────────────────────────────── */

const KPI_SKELETON: { label: string; w: number }[] = [
  { label: 'Unique visitors', w: 67 },
  { label: 'Total views', w: 81 },
  { label: 'Pages', w: 27 },
  { label: 'Avg scroll depth', w: 40 },
  { label: 'Avg time on page', w: 81 },
  { label: 'Top source', w: 81 },
]
const LEGEND_SKELETON = [[16, 20], [54, 20], [121, 20], [67, 16], [94, 16]]
const CHIP_SKELETON = [51, 60, 60, 64, 74, 92, 82]
/** page, section, views, visitors, referrer pill, scroll, time (Paper geometry) */
const ROW_SKELETON = [
  [44, 27, 36, 36, 72, 22, 44],
  [63, 27, 36, 36, 72, 22, 44],
  [141, 47, 36, 22, 82, 22, 22],
  [78, 60, 22, 22, 79, 22, 22],
  [39, 27, 22, 22, 74, 22, 44],
]

function Bar({ w, h, className }: { w: number | string; h: number; className?: string }) {
  return <Skeleton className={cn('shrink-0', className)} style={{ width: w, height: h }} />
}

/** Same geometry as the loaded tab: labels stay, values become hairline bars. */
export function PagesLoading() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading pages">
      <div className="flex flex-col gap-1 py-1">
        <div className="flex h-7 items-center">
          <Bar w="60%" h={28} />
        </div>
        <div className="flex h-4 items-center">
          <Bar w="38%" h={13} />
        </div>
      </div>

      <div className="well flex flex-col gap-2">
        <Card className="gap-0 py-0">
          <KpiStrip>
            {KPI_SKELETON.map((k, i) => (
              <KpiTile key={k.label} first={i === 0} label={k.label} value={<span className="flex h-7 items-center"><Bar w={k.w} h={24} /></span>} />
            ))}
          </KpiStrip>
          <div className="flex flex-col gap-3.5 px-5 pt-4 pb-5">
            <div className="flex items-baseline gap-2">
              <span className="text-[14.5px] font-semibold leading-[18px] text-foreground">Where views land</span>
              <span className="flex h-4 items-center">
                <Bar w={235} h={12} className="max-w-[40vw]" />
              </span>
            </div>
            <Skeleton className="h-3 w-full rounded-[6px]" />
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {LEGEND_SKELETON.map(([a, b], i) => (
                <span key={i} className="flex items-center gap-[7px] pl-[15px]">
                  <span className="flex h-4 items-center"><Bar w={a} h={12} /></span>
                  <span className="flex h-4 items-center"><Bar w={b} h={12} /></span>
                </span>
              ))}
            </div>
          </div>
        </Card>

        <Card className="gap-0 py-0">
          <PagesListToolbar view="pages" onViewChange={() => {}} search="" onSearchChange={() => {}} />
          <SectionChipRow>
            {CHIP_SKELETON.map((w, i) => (
              <Skeleton key={i} className="h-[26px] shrink-0 rounded-md" style={{ width: w }} />
            ))}
          </SectionChipRow>
          <div className="responsive-table-wrapper">
            <Table>
              <PagesTableHead />
              <TableBody>
                {ROW_SKELETON.map(([page, group, views, visitors, ref, scroll, time], i) => (
                  <TableRow key={i} className={cn(reportTable.row, 'hover:bg-transparent')}>
                    <TableCell className={reportTable.td}>
                      <div className="flex flex-col gap-0.5 pl-[22px]">
                        <span className="flex h-[18px] items-center"><Bar w={page} h={14} /></span>
                        <span className="flex h-4 items-center"><Bar w={group} h={12} /></span>
                      </div>
                    </TableCell>
                    <TableCell className={reportTable.td}><span className="flex justify-end"><Bar w={views} h={13} /></span></TableCell>
                    <TableCell className={cn(reportTable.td, 'hidden sm:table-cell')}><span className="flex justify-end"><Bar w={visitors} h={13} /></span></TableCell>
                    <TableCell className={cn(reportTable.td, 'hidden sm:table-cell')}><span className="flex justify-end"><Bar w={30} h={13} /></span></TableCell>
                    <TableCell className={cn(reportTable.td, 'hidden sm:table-cell')}><span className="flex justify-end"><Skeleton className="h-[22px] rounded-md" style={{ width: ref }} /></span></TableCell>
                    <TableCell className={cn(reportTable.td, 'hidden md:table-cell')}><span className="flex justify-end"><Bar w={scroll} h={13} /></span></TableCell>
                    <TableCell className={cn(reportTable.td, 'hidden lg:table-cell')}><span className="flex justify-end"><Bar w={time} h={13} /></span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex h-12 items-center justify-between border-t border-border/60 px-[18px]">
            <Bar w={121} h={12} />
            <ReportPaginationButtons />
          </div>
        </Card>
      </div>
    </div>
  )
}

/** Disabled Previous / Next, so the loading footer keeps the loaded footer's shape. */
function ReportPaginationButtons() {
  const btn = 'flex h-7 items-center rounded-lg bg-card px-2.5 text-xs font-medium leading-4 ring-1 ring-foreground/[0.09]'
  return (
    <div className="flex gap-1.5" aria-hidden>
      <span className={cn(btn, 'text-muted-foreground/60')}>Previous</span>
      <span className={cn(btn, 'text-foreground')}>Next</span>
    </div>
  )
}

